import { DEFAULT_CURRENCY } from '../constants/money.constants';
import { exactMoneyFormatter, formatMoney } from './money';

export function formatNumberWithCommas(number, fixed = false) {
    const fixedNumber = fixed ? Number(number).toFixed(2) : Number(number);
    if (number >= 1000) {
        return fixedNumber.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }
    return fixedNumber;
}

/**
 * The app's one exact USD formatter — the same `Intl.NumberFormat` instance
 * `formatMoney` (utils/money) writes with, so a figure is never formatted two
 * ways on two screens.
 */
export const USDFormatter = exactMoneyFormatter(DEFAULT_CURRENCY);

export const formatScaleAmount = (value) => `$${Math.round(Number(value) || 0)}`;

export const formatCurrency = (value) => `$${(Number(value) || 0).toFixed(2)}`;

// =====================================================================
// Compact / display formatters
//
// Used by dashboard / cashflow cards (KPI tiles, transaction tables,
// hero balances) to keep money formatting consistent across the app.
// `N/A` is returned for null / undefined / NaN so views never have to
// branch on missing data.
// =====================================================================

const N_A = 'N/A';

/**
 * `$3.4M`, `$840.2K`, `$520`, etc. Optimised for KPI tiles where a full
 * currency string would not fit.
 */
export function formatCompactCurrency(value) {
    if (value === null || value === undefined || Number.isNaN(value)) return N_A;
    const abs = Math.abs(value);
    if (abs >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
    if (abs >= 1_000) return `$${(value / 1_000).toFixed(1)}K`;
    // Sub-$1000: keep cents so small non-zero amounts (e.g. a $0.15 fee total)
    // don't collapse to "$0". Trailing ".00" is trimmed for whole dollars.
    const rounded = Number(value.toFixed(2));
    return `$${Number.isInteger(rounded) ? rounded : rounded.toFixed(2)}`;
}

/**
 * `$1,567,421.44` — full precision with thousand separators. Used both
 * for hero balances and as the table cell formatter.
 *
 * Delegates to `formatMoney` (utils/money): one implementation of "an exact
 * amount". What this wrapper keeps is its `N/A` placeholder. Two visible
 * differences from the hand-rolled version it replaces: a negative reads
 * `-$1,234.56` (sign outside the symbol, as `Intl` writes it) rather than
 * `$-1,234.56`, and an unparseable value reads `N/A` rather than `$NaN`.
 */
export function formatFullCurrency(value) {
    return formatMoney(value, DEFAULT_CURRENCY, N_A);
}

/** `1.2k`, `42`, `980` — for "Active Users", "Overdue Users", etc. */
export function formatCompactNumber(value) {
    if (value === null || value === undefined || Number.isNaN(value)) return N_A;
    const abs = Math.abs(value);
    if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
    if (abs >= 1_000) return `${(value / 1_000).toFixed(1)}k`;
    return `${value}`;
}

/** `14.2 days`, `3 days` — keeps the decimal only when present. */
export function formatDays(value) {
    if (value === null || value === undefined || Number.isNaN(value)) return N_A;
    const isInt = Number.isInteger(value);
    return `${isInt ? value : Number(value).toFixed(1)} days`;
}

/** Clamps a percentage to [0, 100]. Non-finite values default to 0. */
export function clampPercent(value) {
    const percent = Number(value);
    return Number.isFinite(percent) ? Math.min(Math.max(percent, 0), 100) : 0;
}

/**
 * Period-over-period % change; matches clockout-service `calculatePercentageChange`.
 * When `previous === 0`, returns a sentinel (+100 if current > 0, else 0) rather than
 * dividing by zero — so 0→1 and 0→10,000 both report +100%. Callers must not pass
 * missing prior data as 0; use null/omit the delta when comparison data is unavailable.
 */
export function calculatePercentageChange(current, previous) {
    if (previous === 0) {
        return current > 0 ? 100 : 0;
    }
    return parseFloat((((current - previous) / previous) * 100).toFixed(2));
}
